import React, { createContext, Suspense, useMemo, useEffect } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { ErrorHandlerAlert } from '../../molecules';
import { Styled } from './HotelOverview.styles';
import { ComponentProps } from './HotelOverview.types';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import Overview from './Overview';
import Location from './Location';
import { useConditionalPageContextSERP } from '../../hooks';
import { useHotelOverviewServerDataStore } from '../../modules/store/hotelOverviewServerDataStore';
import clsx from 'clsx';
import { useCheckBreakpoint } from '../../hooks';
import { useHQVDatalayerTrackingStore } from '../../modules/store/hqvDatalayerTrackingStore';

const serverDataProps: ComponentProps = {
  propertyOverviewData: {},
  jsonContent: {},
  GOOGLE_MAP_API_KEY: '',
  locationData: {},
  model: {},
};

export const serverData = createContext(serverDataProps);

export const HotelOverviewComponent: React.FC<ComponentProps> = props => {
  const { envVarsObject } = useClientEnvVarsStore();
  const isViewportL = useCheckBreakpoint('viewportL');
  const { pageVal } = useConditionalPageContextSERP();
  const hotelOverviewStore = useHotelOverviewServerDataStore();
  const propertyInfoCallStatus = useHQVDatalayerTrackingStore(state => state.propertyInfoCallStatus);

  const shouldShowLocation = propertyInfoCallStatus !== 'error';

  const value: ComponentProps = useMemo(() => {
    return { jsonContent: props?.model, GOOGLE_MAP_API_KEY: envVarsObject['GOOGLE_MAP_API_KEY'] };
  }, [props?.model?.jsonContent]);

  // Initialize the Zustand store with server data when flag is enabled (independent pattern)
  useEffect(() => {
    const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, FLAG_TYPE> | undefined;
    const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand && props) {
      // Initialize store with server data (not from context)
      hotelOverviewStore.hotelOverviewInitialization({
        propertyOverviewData: props.propertyOverviewData,
        jsonContent: props.jsonContent,
        GOOGLE_MAP_API_KEY: envVarsObject['GOOGLE_MAP_API_KEY'],
        locationData: props.locationData,
        model: props.model,
        pageProps: props.pageProps,
        overridePropertyCountry: props.overridePropertyCountry,
        overridePropertyCountryText: props.overridePropertyCountryText,
      });
    }
  }, [props, envVarsObject, hotelOverviewStore]);

  return (
    <ErrorHandlerAlert alertMessage={props?.jsonContent?.['componentErrorMessage']}>
      <Suspense fallback={''}>
        <serverData.Provider value={value}>
          <Styled>
            <div
              className={clsx(
                'standard color-scheme1 overview-and-location',
                pageVal === 'chat' && !isViewportL ? 'px-4' : ''
              )}
            >
              {pageVal === 'chat' && props.model.overviewDescriptionDisabled !== true && <Overview />}
              {pageVal === 'chat' && props.model.locationDisabled !== true && shouldShowLocation && <Location />}
              {pageVal !== 'chat' && <Overview />}
              {pageVal !== 'chat' && shouldShowLocation && <Location />}
            </div>
          </Styled>
        </serverData.Provider>
      </Suspense>
    </ErrorHandlerAlert>
  );
};
export const HotelOverviewComponentConfig = {
  emptyLabel: 'HotelOverviewComponent',
  isEmpty: false,
  resourceType: `mi-aem-shop-spa/components/content/hotelgalleryoverview`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HotelOverviewComponentEditable = (props: any) => {
  return props.cqPath.includes('experiencefragment') ? (
    <HotelOverviewComponent {...props} />
  ) : (
    <EditableComponent config={HotelOverviewComponentConfig} {...props}>
      <HotelOverviewComponent {...props} />
    </EditableComponent>
  );
};
